import { showCmsFormValidationErrorToast, useGetSelectionFormData } from "@/api/cms";
import {
  useCreateSection,
  useDeleteSection,
  useGetSectionById,
  useUpdateSection,
} from "@/api/section";
import { SectionForm } from "@/components/cms/selection/SectionForm";
import { CroppedImageInput } from "@/components/form-fields/CroppedImageInput";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Button } from "@/components/ui/button";
import { CustomAlertDialog } from "@/components/ui/custom-alert-dialog";
import { Form } from "@/components/ui/form";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { error, success, useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout";
import { sectionContentSchema } from "@/lib/cms/section-types";
import { generateImageName } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Attachment } from "@/lib/types";

const formSchema = z.object({
  content: multilingualFieldSchema(
    sectionContentSchema.omit({ id: true, sectionId: true }),
  ),
});
type FormData = z.infer<typeof formSchema>;

const EditSection = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const { mutateAsync: createSection } = useCreateSection();
  const { mutateAsync: updateSection } = useUpdateSection();
  const { mutateAsync: deleteSection } = useDeleteSection();

  const { data: selectionPageData } = useGetSelectionFormData();

  const navigate = useNavigate();
  const { id } = useParams();
  const { data: sectionData, refetch: refetchSection } = useGetSectionById(id);

  const [thumbnailId, setThumbnailId] = useState<string | undefined>();
  const [thumbnailAttachment, setThumbnailAttachment] = useState<
    Attachment | undefined
  >();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      content: constructCmsLocaleDict(() => {
        return {
          title: "",
          content: "",
          published: false,
        };
      }, websiteLanguages),
    },
    mode: "onChange",
  });

  const lastSectionId = useRef<string | undefined>(id);
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (sectionData) {
      const idChanged = lastSectionId.current !== id;
      if (idChanged) {
        lastSectionId.current = id;
        hasInitialized.current = false;
      }
      if (!hasInitialized.current) {
        setThumbnailId(sectionData.thumbnail?.id);
        setThumbnailAttachment(sectionData.thumbnail ?? undefined);
        form.reset({
          content: constructCmsLocaleDict((lang) => {
            const section = sectionData?.content?.[lang];
            return {
              title: section?.title ?? "",
              content: section?.content ?? "",
              published: section?.published ?? false,
            };
          }, websiteLanguages),
        });
        hasInitialized.current = true;
      }
    } else if (!id) {
      setThumbnailId(undefined);
      setThumbnailAttachment(undefined);
    }
  }, [form, id, sectionData, websiteLanguages]);

  const onSubmit = async (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    if (!thumbnailId) {
      toast({
        title: t("error"),
        description: t("cms.selection.editSection.error.thumbnailRequired"),
        variant: "error",
        duration: 4000,
      });
      return;
    }

    if (!id) {
      await createSection(
        {
          content: submittedData.content,
          thumbnailId,
        },
        {
          onError: (e) => {
            console.error("Error", e);
            toast(
              error({
                description: t(
                  "cms.selection.editSection.error.createSectionFailed",
                ),
              }),
            );
          },
          onSuccess: () => {
            toast(
              success({
                description: t(
                  "cms.selection.editSection.success.createSection",
                ),
              }),
            );
            navigate("/cms/selection/");
          },
        },
      );
    } else {
      await updateSection(
        {
          id: id,
          section: {
            content: submittedData.content,
            thumbnailId,
          },
        },
        {
          onError: (e) => {
            console.error("Error", e);
            toast(
              error({
                description: t(
                  "cms.selection.editSection.error.updateSectionFailed",
                ),
              }),
            );
          },
          onSuccess: () => {
            toast(
              success({
                description: t(
                  "cms.selection.editSection.success.updateSection",
                ),
              }),
            );
            navigate("/cms/selection/section/" + id);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!id) {
      throw new Error("Tried to delete section but id is undefined");
    }
    deleteSection(id, {
      onError: (e) => {
        console.error("Error", e);
        toast(
          error({
            description: t(
              "cms.selection.editSection.error.deleteSectionFailed",
            ),
          }),
        );
      },
      onSuccess: () => {
        toast(
          success({
            description: t("cms.selection.editSection.success.deleteSection"),
          }),
        );
      },
    });
    navigate("/cms/selection/");
  };

  const handleThumbnailSuccess = (attachment: Attachment) => {
    setThumbnailId(attachment.id);
    setThumbnailAttachment(attachment);
    refetchSection();
  };

  const mainLangTitle = form.watch(`content.${getMainLanguage()}.title`);

  return (
    <CMSLayout
      formIds={["editSection"]}
      pageName={
        id
          ? t("cms.selection.editSection.title")
          : t("cms.selection.editSection.title.addSection")
      }
      breadcrumbs={[
        {
          label:
            selectionPageData?.[getMainLanguage()]?.pageName ??
            t("cmsTabSelection"),
          to: "/cms/selection",
        },

        ...(id
          ? [
              {
                label: sectionData?.content[getMainLanguage()]?.title ?? "",
                to: "/cms/selection/section/" + id,
              },
              {
                label: t("cms.selection.editSection.title"),
              },
            ]
          : [
              {
                label: t("cms.selection.editSection.title.addSection"),
              },
            ]),
      ]}
    >
      <Form {...form}>
        <form
          id="editSection"
          onSubmit={form.handleSubmit(onSubmit, showCmsFormValidationErrorToast)}
          className="flex flex-col gap-8 w-full"
        >
          <div className="flex flex-row w-full justify-between items-center">
            <p className="text-gray-950 font-semibold text-lg">
              {t("cms.selection.editSection.thumbnail.title")}
            </p>
            {id && (
              <CustomAlertDialog
                title={t("cms.selection.editSection.deleteAlert.title")}
                description={t(
                  "cms.selection.editSection.section.deleteAlert.description",
                )}
                confirmButton={
                  <Button
                    type="button"
                    onClick={handleDelete}
                    variant={"destructive"}
                  >
                    {t("cms.selection.editSection.section.delete")}
                  </Button>
                }
              >
                <Button type="button" variant={"destructive"}>
                  {t("cms.selection.editSection.section.delete")}
                </Button>
              </CustomAlertDialog>
            )}
          </div>

          <CroppedImageInput
            attachment={thumbnailAttachment}
            imageURL={thumbnailAttachment?.mediumPublicUrl}
            refetch={refetchSection}
            imagePosition="sectionThumbnail"
            docId={id ?? ""}
            aspect={16 / 9}
            onSuccess={handleThumbnailSuccess}
            buildImageName={(instanceName) =>
              generateImageName({
                primaryType: "thumbnail",
                instanceName,
                pageNameInDefaultLanguage: "section",
                title: mainLangTitle,
              })
            }
          />

          <div className="flex gap-4">
            <DoubleMultilingualField
              corner={(lang) => (
                <Switch dispLabel={true} name={`content.${lang}.published`} />
              )}
              render={(lang) => <SectionForm lang={lang} />}
            />
          </div>
        </form>
      </Form>
    </CMSLayout>
  );
};

export default EditSection;
